import { FeedItemTypeDeal, type FeedItem } from '@/lib/api/apiTypes';
import type { FeedTimeWindow } from '@/features/events/feed/feedPreferences';

export type DateGroupedFeedRow =
  | {
      date: string;
      dotColor?: string;
      id: string;
      isFirst: boolean;
      kind: 'date_header';
      label: string;
    }
  | {
      date?: string;
      id: string;
      item: FeedItem;
      kind: 'feed_item';
      label?: string;
      position: number;
    };

type DateGroupedFeedInput = {
  effectiveTimeWindow: FeedTimeWindow;
  forceDateHeaders?: boolean;
  items: FeedItem[];
  sourceTimeWindow: FeedTimeWindow;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const URGENCY_GROUPS = [
  'happening_now',
  'starting_soon',
  'later_tonight',
  'tomorrow',
  'this_week',
] as const;
const URGENCY_GROUP_DOT_COLORS: Record<(typeof URGENCY_GROUPS)[number], string> = {
  happening_now: '#E24B4A',
  starting_soon: '#D85A30',
  later_tonight: 'rgba(255,255,255,0.4)',
  tomorrow: 'rgba(255,255,255,0.25)',
  this_week: 'rgba(255,255,255,0.15)',
};
const DEAL_FALLBACK_GROUPS = ['later_tonight', 'this_week'] as const;

export function buildDateGroupedFeedRows({
  effectiveTimeWindow,
  forceDateHeaders = false,
  items,
  sourceTimeWindow,
}: DateGroupedFeedInput): DateGroupedFeedRow[] {
  if (!forceDateHeaders && !shouldGroupFeedItems(sourceTimeWindow, effectiveTimeWindow)) {
    return items.map((item, index) => buildFeedItemRow(item, index));
  }

  const groupedItems = forceDateHeaders
    ? items
    : getVisibleGroupedItems(items, sourceTimeWindow, effectiveTimeWindow);
  const groups = new Map<string, { label: string; items: FeedItem[] }>();

  for (const item of groupedItems) {
    const date = getFeedItemGroupDate(item);
    const key = formatLocalDateKey(date);
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    groups.set(key, {
      label: formatDateGroupLabel(date),
      items: [item],
    });
  }

  const rows: DateGroupedFeedRow[] = [];
  let position = 1;
  Array.from(groups.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .forEach(([date, group], groupIndex) => {
      if (group.items.length === 0) {
        return;
      }

      rows.push({
        date,
        id: `date:${date}`,
        isFirst: groupIndex === 0,
        kind: 'date_header',
        label: group.label,
      });

      for (const item of group.items) {
        rows.push(buildFeedItemRow(item, position - 1, { date, label: group.label }));
        position += 1;
      }
    });

  return rows;
}

export function buildUrgencyGroupedFeedRows(items: FeedItem[]): DateGroupedFeedRow[] {
  const now = new Date();
  let untimedDealIndex = 0;
  const groups = new Map<
    string,
    { date: string; dotColor: string; items: FeedItem[]; label: string; order: number }
  >();

  for (const item of items) {
    const group = getUrgencyGroup(item, now, untimedDealIndex);
    if (item.type === FeedItemTypeDeal && !getDealNextOccurrence(item)) {
      untimedDealIndex += 1;
    }
    const existing = groups.get(group.key);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    groups.set(group.key, {
      date: group.key,
      dotColor: group.dotColor,
      items: [item],
      label: group.label,
      order: group.order,
    });
  }

  const rows: DateGroupedFeedRow[] = [];
  let position = 1;
  Array.from(groups.values())
    .sort((left, right) => left.order - right.order || left.date.localeCompare(right.date))
    .forEach((group, groupIndex) => {
      const orderedItems = sortFeedItemsByDistance(group.items);

      if (orderedItems.length === 0) {
        return;
      }

      rows.push({
        date: group.date,
        dotColor: group.dotColor,
        id: `urgency:${group.date}`,
        isFirst: groupIndex === 0,
        kind: 'date_header',
        label: group.label,
      });

      for (const item of orderedItems) {
        rows.push(buildFeedItemRow(item, position - 1, { date: group.date, label: group.label }));
        position += 1;
      }
    });

  return rows;
}

function shouldGroupFeedItems(sourceTimeWindow: FeedTimeWindow, effectiveTimeWindow: FeedTimeWindow) {
  if (sourceTimeWindow === 'today' && effectiveTimeWindow !== 'today') {
    return true;
  }
  return effectiveTimeWindow === 'week' || effectiveTimeWindow === 'weekend';
}

function getVisibleGroupedItems(
  items: FeedItem[],
  sourceTimeWindow: FeedTimeWindow,
  effectiveTimeWindow: FeedTimeWindow
) {
  if (sourceTimeWindow !== 'today' || effectiveTimeWindow === 'today') {
    return items;
  }

  const todayKey = formatLocalDateKey(new Date());
  const todayItems = items.filter(
    (item) => formatLocalDateKey(getFeedItemGroupDate(item)) === todayKey
  );
  if (todayItems.length >= 3) {
    return todayItems;
  }

  const nextEventDate = items
    .map((item) => formatLocalDateKey(getFeedItemGroupDate(item)))
    .filter((date) => date !== todayKey)
    .sort()[0];

  if (!nextEventDate) {
    return todayItems;
  }

  return items.filter((item) => {
    const date = formatLocalDateKey(getFeedItemGroupDate(item));
    return todayItems.length === 0
      ? date === nextEventDate
      : date === todayKey || date === nextEventDate;
  });
}

function buildFeedItemRow(
  item: FeedItem,
  index: number,
  group?: { date: string; label: string }
): DateGroupedFeedRow {
  return {
    date: group?.date,
    id: item.event?.id ?? item.deal?.deal.id ?? `${item.type}:${index}`,
    item,
    kind: 'feed_item',
    label: group?.label,
    position: index + 1,
  };
}

function getFeedItemGroupDate(item: FeedItem) {
  if (item.event?.starts_at) {
    return new Date(item.event.starts_at);
  }

  if (item.type === FeedItemTypeDeal) {
    return new Date();
  }

  return new Date();
}

function getUrgencyGroup(item: FeedItem, now: Date, untimedDealIndex = 0) {
  if (item.type === FeedItemTypeDeal) {
    return getDealUrgencyGroup(item, now, untimedDealIndex);
  }

  const date = getFeedItemUrgencyDate(item);
  const dayDiff = getLocalDayDiff(now, date);
  const startsInMs = date.getTime() - now.getTime();

  if (item.event) {
    const endsAt = item.event.ends_at
      ? new Date(item.event.ends_at)
      : new Date(date.getTime() + 4 * 60 * 60 * 1000);
    if (date.getTime() <= now.getTime() && endsAt.getTime() >= now.getTime()) {
      return buildUrgencyGroup('happening_now', 'Happening now', 0);
    }
    if (startsInMs > 0 && startsInMs <= 2 * 60 * 60 * 1000) {
      return buildUrgencyGroup('starting_soon', 'Starting soon', 1);
    }
  }

  if (dayDiff === 1) {
    return buildUrgencyGroup('tomorrow', 'Tomorrow', 3);
  }
  if (dayDiff >= 2 && dayDiff <= 7) {
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
    return buildUrgencyGroup(
      'this_week',
      `This ${weekday}`,
      4 + dayDiff / 10,
      formatLocalDateKey(date)
    );
  }

  return buildUrgencyGroup('later_tonight', 'Later tonight', 2);
}

function getDealUrgencyGroup(item: FeedItem, now: Date, untimedDealIndex: number) {
  const nextOccurrence = getDealNextOccurrence(item);
  if (nextOccurrence) {
    return getScheduledDealUrgencyGroup(new Date(nextOccurrence), now);
  }

  if (item.deal?.timing_state === 'live') {
    return buildUrgencyGroup('later_tonight', 'Later tonight', 2);
  }

  const fallbackGroup = DEAL_FALLBACK_GROUPS[untimedDealIndex % DEAL_FALLBACK_GROUPS.length];
  if (fallbackGroup === 'this_week') {
    return buildUrgencyGroup('this_week', 'This Week', 4.8, 'untimed-deals');
  }
  return buildUrgencyGroup('later_tonight', 'Later tonight', 2);
}

function getScheduledDealUrgencyGroup(date: Date, now: Date) {
  const dayDiff = getLocalDayDiff(now, date);
  const startsInMs = date.getTime() - now.getTime();

  if (startsInMs > 0 && startsInMs <= 2 * 60 * 60 * 1000) {
    return buildUrgencyGroup('starting_soon', 'Starting soon', 1);
  }
  if (dayDiff === 1) {
    return buildUrgencyGroup('tomorrow', 'Tomorrow', 3);
  }
  if (dayDiff >= 2 && dayDiff <= 7) {
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
    return buildUrgencyGroup(
      'this_week',
      `This ${weekday}`,
      4 + dayDiff / 10,
      formatLocalDateKey(date)
    );
  }

  return buildUrgencyGroup('later_tonight', 'Later tonight', 2);
}

function sortFeedItemsByDistance(items: FeedItem[]) {
  return [...items].sort((left, right) => getFeedItemDistance(left) - getFeedItemDistance(right));
}

function getFeedItemDistance(item: FeedItem) {
  return item.event?.distance_meters ?? item.deal?.deal.distance_meters ?? Number.MAX_SAFE_INTEGER;
}

function buildUrgencyGroup(
  group: (typeof URGENCY_GROUPS)[number],
  label: string,
  order: number,
  keySuffix?: string
) {
  return {
    dotColor: URGENCY_GROUP_DOT_COLORS[group],
    key: keySuffix ? `${group}:${keySuffix}` : group,
    label,
    order,
  };
}

function getFeedItemUrgencyDate(item: FeedItem) {
  if (item.event?.starts_at) {
    return new Date(item.event.starts_at);
  }

  const nextDealOccurrence = getDealNextOccurrence(item);
  if (nextDealOccurrence) {
    return new Date(nextDealOccurrence);
  }

  return new Date();
}

function getDealNextOccurrence(item: FeedItem) {
  return item.type === FeedItemTypeDeal
    ? (item.deal as { next_occurs_at?: string | null } | null)?.next_occurs_at
    : null;
}

function formatLocalDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateGroupLabel(date: Date) {
  const today = startOfLocalDay(new Date());
  const target = startOfLocalDay(date);
  const diffDays = Math.round((target.getTime() - today.getTime()) / MS_PER_DAY);

  if (diffDays === 0) {
    return 'TODAY';
  }
  if (diffDays === 1) {
    return 'TOMORROW';
  }
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date).toUpperCase();
  const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date).toUpperCase();
  return `${weekday} · ${month} ${date.getDate()}`;
}

function getLocalDayDiff(left: Date, right: Date) {
  return Math.round(
    (startOfLocalDay(right).getTime() - startOfLocalDay(left).getTime()) / MS_PER_DAY
  );
}

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
